import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { Separator } from '@workspace/ui/components/separator'
import { Check, Clock, Link, Mail, Plus, Users, X } from 'lucide-react'
import { useState } from 'react'
import type { SettingsInvite, SettingsUser } from '../types'
import { UserRow } from './user-row'

export interface AccessManagementSectionProps {
  users: SettingsUser[]
  invites: SettingsInvite[]
  onInvite: (email: string, role: 'OWNER' | 'WRITE' | 'READ') => void
  onCancelInvite: (inviteId: string) => void
  onRemoveMember?: (memberId: string) => void
  isInviting?: boolean
  isOwner?: boolean
}

export function AccessManagementSection({
  users,
  invites,
  onInvite,
  onCancelInvite,
  onRemoveMember,
  isInviting,
  isOwner,
}: AccessManagementSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [removingMember, setRemovingMember] = useState<SettingsUser | null>(null)

  const handleCopyLink = async (invite: SettingsInvite) => {
    const url = `${window.location.origin}/app?invite=${invite.token}`
    await navigator.clipboard.writeText(url)
    setCopiedId(invite.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const validateEmail = (value: string) => {
    if (!value.trim()) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email'
    return ''
  }

  const handleInvite = () => {
    const error = validateEmail(email)
    if (error) {
      setEmailError(error)
      return
    }
    onInvite(email.trim(), 'WRITE')
    setEmail('')
    setEmailError('')
    setDialogOpen(false)
  }

  const handleConfirmRemove = () => {
    if (removingMember && onRemoveMember) {
      onRemoveMember(removingMember.id)
    }
    setRemovingMember(null)
  }

  return (
    <section>
      <div className="overflow-hidden rounded-xl border border-border bg-card/30">
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            <Users className="size-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Access Management</h2>
            <span className="text-xs text-muted-foreground">
              {users.length} {users.length === 1 ? 'member' : 'members'}
            </span>
          </div>
          {isOwner && (
            <SecondaryButton
              size="sm"
              icon={<Plus className="size-3.5" />}
              onClick={() => setDialogOpen(true)}
            >
              Invite
            </SecondaryButton>
          )}
        </div>

        <Separator />
        <div className="divide-y divide-border">
          {users.map((member) => (
            <UserRow
              key={member.id}
              member={member}
              isOwner={isOwner}
              onRemove={
                isOwner && member.role !== 'OWNER' && onRemoveMember
                  ? () => setRemovingMember(member)
                  : undefined
              }
            />
          ))}
        </div>

        {invites.length > 0 && (
          <>
            <Separator />
            <div className="px-5 py-3">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Clock className="size-3" />
                Pending Invites
              </span>
            </div>
            <div className="divide-y divide-border">
              {invites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-4 px-5 py-3">
                  <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                    <Mail className="size-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground">{invite.email}</p>
                  </div>
                  {isOwner && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleCopyLink(invite)}
                        className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
                        title="Copy invite link"
                      >
                        {copiedId === invite.id ? (
                          <Check className="size-4 text-emerald-500" />
                        ) : (
                          <Link className="size-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => onCancelInvite(invite.id)}
                        className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
            <DialogDescription>
              Send an invite link via email. They'll be able to join your startup.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label htmlFor="invite-email" className="text-sm font-medium text-foreground">
                Email address
              </label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (emailError) setEmailError('')
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                className={emailError ? 'border-red-400' : ''}
              />
              {emailError && <p className="text-[10px] text-red-400">{emailError}</p>}
            </div>
          </div>
          <DialogFooter>
            <SecondaryButton onClick={() => setDialogOpen(false)}>Cancel</SecondaryButton>
            <CtaButton onClick={handleInvite} disabled={!email.trim() || isInviting}>
              {isInviting ? 'Sending...' : 'Send invite'}
            </CtaButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!removingMember}
        onOpenChange={(open) => !open && setRemovingMember(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <span className="font-medium text-foreground">{removingMember?.name}</span> from this
              startup? They will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
